import React, { useEffect, useMemo, useState } from "react";
import "./admin-user-detail.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import { getUser } from "../../api/user.api";
import { HashLoader } from "react-spinners";
import {
  getUserRankImageURL,
  paymentMethodText,
  productTypeText,
  statusText,
  userRankText,
  userStatusText,
} from "../../utils";
import { hasPermission } from "../../utils/auth";
import { useUser } from "../../context/UserContext";
import { getUserLogin } from "../../api/interaction.api";
import { getUserRecentOrders } from "../../api/order.api";
import { getUserActionHistory, getUserDepositHistory } from "../../api/transaction.api";
import { FaEye } from "react-icons/fa6";
import { TbRefresh } from "react-icons/tb";

export const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userLogin, setUserLogin] = useState([]);
  const [userOrders, setUserOrders] = useState([]);
  const [userDeposit, setUserDeposit] = useState([]);
  const [actions, setActions] = useState([]);

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await getUser(userId);
      const userLoginRes = await getUserLogin(userId);
      const userRecentOrdersRes = await getUserRecentOrders(userId);
      const userDepositHistoryRes = await getUserDepositHistory(userId);
      const actionsResponse = await getUserActionHistory(userId);

      if (res?.success && userLoginRes?.success && actionsResponse?.success) {
        setUserData(res.data.user || null);
        setUserLogin(userLoginRes.data.interactions || []);
        setActions(actionsResponse.data.actions || []);
        setUserOrders(userRecentOrdersRes?.data?.orders || []);
        setUserDeposit(userDepositHistoryRes?.data?.deposit_history || []);
        document.title = `Người dùng: ${res.data.user?.username || userId}`;
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(
    () => ({
      loginCount: userLogin.length,
      orderCount: userOrders.length,
      depositCount: userDeposit.length,
      actionCount: actions.length,
      balance: Number(userData?.balance || 0),
      deposited: Number(userData?.total_amount_deposited || 0),
    }),
    [actions.length, userData?.balance, userData?.total_amount_deposited, userDeposit.length, userLogin.length, userOrders.length]
  );

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="var(--hash-loader-color)" />
      </div>
    );
  }

  return (
    <section className="aud-page">
      <ToastNotification />

      <div className="aud-header">
        <div>
          <p className="aud-breadcrumb">
            <Link to="/super-admin/users">Quản trị / Người dùng</Link> / Hồ sơ
          </p>
          <h1 className="aud-title">
            Hồ sơ người dùng <span>#{userId}</span>
          </h1>
        </div>

        <div className="aud-header-actions">
          <button className="aud-btn aud-btn-ghost" onClick={fetchUser} type="button" disabled={loading}>
            <TbRefresh />
            Làm mới
          </button>
          {hasPermission(user?.role, "edit:user") && (
            <button className="aud-btn aud-btn-primary" onClick={() => navigate(`/super-admin/users/edit/${userId}`)} type="button">
              Chỉnh sửa
            </button>
          )}
          <button className="aud-btn aud-btn-primary" onClick={() => navigate("/super-admin/users")} type="button">
            Quay lại
          </button>
        </div>
      </div>

      <div className="aud-layout">
        <aside className="aud-sidebar">
          <div className="aud-profile-card">
            <img className="aud-avatar" src={userData?.profileImg} alt="avatar" />
            <h2>{userData?.username || "-"}</h2>
            <p>{userData?.email || "-"}</p>

            <div className={`aud-pill aud-pill-status aud-pill-status-${userData?.status || "active"}`}>
              {userStatusText(userData?.status)}
            </div>

            <div className={`aud-pill aud-pill-rank aud-pill-rank-${userData?.rank || "newbie"}`}>
              <img src={getUserRankImageURL(userData?.rank) || ""} alt="rank" />
              <span>{userRankText(userData?.rank)}</span>
            </div>
          </div>

          <div className="aud-metric-card">
            <Metric label="Số dư" value={`${summary.balance.toLocaleString("vi-VN")}đ`} />
            <Metric label="Tổng nạp" value={`${summary.deposited.toLocaleString("vi-VN")}đ`} />
            <Metric label="Lượt đăng nhập" value={summary.loginCount} />
            <Metric label="Lịch sử chỉnh số dư" value={summary.actionCount} />
            <Metric label="Giao dịch nạp" value={summary.depositCount} />
            <Metric label="Đơn hàng" value={summary.orderCount} />
          </div>
        </aside>

        <main className="aud-main">
          <section className="aud-block">
            <h3>Thông tin tài khoản</h3>
            <div className="aud-info-grid">
              <InfoCell label="Họ và tên" value={userData?.fullName || "Chưa cung cấp"} />
              <InfoCell label="Email" value={userData?.email || "-"} />
              <InfoCell label="Số điện thoại" value={userData?.phone || "Chưa cung cấp"} />
              <InfoCell
                label="Vai trò"
                value={userData?.role ? `${userData.role.charAt(0).toUpperCase()}${userData.role.slice(1)}` : "-"}
              />
              <InfoCell
                label="Ngày tạo"
                value={userData?.createdAt ? new Date(userData.createdAt).toLocaleString("vi-VN") : "-"}
              />
            </div>
          </section>

          <HistoryTable title="Lịch sử đăng nhập" columns={["STT", "Hành động", "Thời gian"]}>
            {userLogin.length ? (
              userLogin.map((row, index) => (
                <tr key={`${row?.createdAt || index}-login`}>
                  <td>{index + 1}</td>
                  <td>Đăng nhập</td>
                  <td>{new Date(row?.createdAt).toLocaleString("vi-VN")}</td>
                </tr>
              ))
            ) : (
              <EmptyRow colSpan={3} text="Không có dữ liệu đăng nhập" />
            )}
          </HistoryTable>

          <HistoryTable title="Lịch sử chỉnh sửa số dư" columns={["STT", "Số tiền", "Người", "Lý do", "Thời gian"]}>
            {actions.length ? (
              actions.map((row, index) => (
                <tr key={`${row?.createdAt || index}-action`}>
                  <td>{index + 1}</td>
                  <td>{row.transactionType === "add" ? "+" : "-"} {Number(row.amount || 0).toLocaleString("vi-VN")}đ</td>
                  <td>{row.author || "-"}</td>
                  <td>{row.reason || "-"}</td>
                  <td>{new Date(row?.createdAt).toLocaleString("vi-VN")}</td>
                </tr>
              ))
            ) : (
              <EmptyRow colSpan={5} text="Không có dữ liệu chỉnh sửa" />
            )}
          </HistoryTable>

          <HistoryTable
            title="Lịch sử nạp tiền"
            columns={["STT", "Mã giao dịch", "Số tiền", "Phương thức", "Trạng thái", "Thông tin", "Thời gian"]}
          >
            {userDeposit.length ? (
              userDeposit.map((row, index) => (
                <tr key={`${row?.transactionId || index}-deposit`}>
                  <td>{index + 1}</td>
                  <td>{row.transactionId || "-"}</td>
                  <td>{Number(row.amount || 0).toLocaleString("vi-VN")}đ</td>
                  <td>{paymentMethodText(row.paymentMethod) || "NA"}</td>
                  <td>{statusText(row.transactionStatus) || "-"}</td>
                  <td>
                    {row?.transaction_attributes ? (
                      <>
                        <p>Serial: {row.transaction_attributes.serial}</p>
                        <p>Code: {row.transaction_attributes.code}</p>
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{new Date(row?.createdAt).toLocaleString("vi-VN")}</td>
                </tr>
              ))
            ) : (
              <EmptyRow colSpan={7} text="Không có dữ liệu nạp tiền" />
            )}
          </HistoryTable>

          <HistoryTable
            title="Lịch sử mua hàng"
            columns={["STT", "Mã đơn", "Giá", "Loại", "Trạng thái", "Thời gian", "Xem"]}
          >
            {userOrders.length ? (
              userOrders.map((row, index) => (
                <tr key={`${row?.orderId || index}-order`}>
                  <td>{index + 1}</td>
                  <td>{row.orderId || "-"}</td>
                  <td>{Number(row.order_final_amount || 0).toLocaleString("vi-VN")}đ</td>
                  <td>{productTypeText(row.order_type)}</td>
                  <td>{statusText(row.order_status) || "-"}</td>
                  <td>{new Date(row?.createdAt).toLocaleString("vi-VN")}</td>
                  <td>
                    <button
                      className="aud-icon-btn"
                      type="button"
                      onClick={() => navigate(`/super-admin/orders/view/${row.orderId}`)}
                    >
                      <FaEye />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <EmptyRow colSpan={7} text="Không có dữ liệu đơn hàng" />
            )}
          </HistoryTable>
        </main>
      </div>
    </section>
  );
};

const Metric = ({ label, value }) => (
  <div className="aud-metric-item">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const InfoCell = ({ label, value }) => (
  <article className="aud-info-cell">
    <p>{label}</p>
    <strong>{value}</strong>
  </article>
);

const EmptyRow = ({ colSpan, text }) => (
  <tr>
    <td colSpan={colSpan} className="aud-empty-cell">
      {text}
    </td>
  </tr>
);

const HistoryTable = ({ title, columns, children }) => (
  <section className="aud-block">
    <h3>{title}</h3>
    <div className="aud-table-wrap">
      <table className="aud-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </section>
);
